/**
 * What the god is looking at (Expansion II §3, §13; v0.3.2 "one tree can
 * become sacred").
 *
 * Every verb until now has been a disc of ground. That is right for weather
 * and wrong for intent: a god that can bless a valley but cannot mean *that
 * tree* has no vocabulary for the thing religion is actually made of, which
 * is a particular place, a particular person, a particular stone. The focus
 * ladder already bottoms out at five metres and calls the band `presence`;
 * what was missing was a way to say which five metres, and to have the world
 * record that you meant it.
 *
 * So: everything is selectable. The pointer resolves to a *thing* — a plant,
 * an outcrop, a person, a herd, a roof, a trail, a god's own stone — and a
 * verb cast on one is channelled into it: centred on the thing, narrowed to
 * the thing's own footprint, and written into the event as what was acted on.
 *
 * Two laws shape this module, and they are worth stating because they are
 * what keep it honest:
 *
 * **OBSERVATION MUST NOT CREATE HISTORY.** Looking at something writes
 * nothing, anywhere. Nothing here takes a simulation, a clock or a writer;
 * it takes a description of what the ray hit and returns a name for it.
 * `test/attention.ts` hovers every kind of thing in a live world and asserts
 * the snapshot is byte-identical afterwards.
 *
 * **Identity comes free from law 1.** Baseline world content is a pure
 * function of world position and seed, so a tree's *position is its name*:
 * no id, no store, no bookkeeping, and the same tree on every machine for
 * ever. Simulation entities — people, bands, structures, anchors — already
 * carry ids, and use them. Nothing has to be promoted into an entity just to
 * be pointed at, which is exactly what §4 asks: persistence follows
 * significance, and being looked at is not significance.
 */

/**
 * What kinds of thing a god can mean.
 *
 * `relic` is the rare, findable class — one object per several square
 * kilometres rather than litter's one per few square metres. It is its own
 * kind and not a litter subtype because the two differ in every way that
 * matters to the pointer: a relic is pointable from much further out, it
 * survives the litter budget's thinning, it carries a name of its own rather
 * than a description of its class, and being *the only one for miles* is the
 * whole of its content. Its vocabulary and its lattice live in
 * `world/relics.ts`, so that adding relics is never an edit to this file.
 */
export type TargetKind =
  | 'ground' | 'landmark' | 'plant' | 'rock' | 'litter' | 'relic'
  | 'person' | 'herd' | 'animal' | 'structure' | 'trail' | 'anchor';

export interface Target {
  kind: TargetKind;
  /**
   * Stable identity. Simulation entities use their own id; baseline things
   * use `placeKey` — their position, which for a deterministic world *is*
   * their name.
   */
  id: string;
  /** The thing's own base, in world metres. Not where the ray happened to land. */
  x: number; y: number; z: number;
  /**
   * Metres of footprint — what "this thing" means when a verb is channelled
   * into it, and how wide to draw the ring around it.
   */
  size: number;
  /** What it is, in the world's voice. */
  name: string;
  /** A second clause, when there is something true to add. */
  note?: string;
  /**
   * The few facts the pick already had in its hand when it named this, kept so
   * that the card does not have to re-derive them.
   *
   * It is deliberately a bag of plain values and deliberately tiny. The
   * alternative — and the first version of the card did this — is parsing them
   * back out of `name`, `note` and `id`, which is a formatted sentence, a
   * formatted sentence and an identity: three things that exist to be read by a
   * human, a human and a Map. A card that regexes a height out of *"11 m of it,
   * and nobody fells this one"* is one prose edit away from being wrong and
   * silent about it.
   *
   * Nothing here is state. It is what was true at the instant of the pick, and
   * anything that moves — a person's activity, a herd's count, a hearth's fire
   * — is read live from the simulation instead.
   */
  about?: {
    /** Which plant, which animal, which litter — the sub-kind, not the class. */
    species?: string;
    /** Metres tall, where that is a different question from `size`. */
    height?: number;
    /** A trail's wear, 0..1. */
    strength?: number;
  };
}

/**
 * The name of a place, to a quarter of a metre — a tree's position *is* its
 * name, which is why nothing here mints an id.
 *
 * It now lives in `core/rng.ts`, next to the determinism law it follows from,
 * because `world/things.ts` has to key promotion on exactly the identity the
 * pointer resolves: what you point at and what the world keeps must not be
 * able to drift apart. Re-exported here, where every caller already looks
 * for it.
 */
export { placeKey } from '@/core/rng';

/** Metres, rounded the way you would say them aloud. */
const say = (m: number): string =>
  m < 10 ? `${m.toFixed(1)} m` : `${Math.round(m)} m`;

/**
 * A plant, named by what it is and how much of it there is. The kinds are the
 * ones the vegetation kit grows — **twenty-one of them now**, and this comment
 * said "the four" through two widenings before anybody noticed, which is why
 * it now says where to look instead of carrying a copy of the number: the list
 * is `VegKind` in `world/chunk.ts`, and the compiler will not tell you when it
 * grows, because this function takes a plain string.
 *
 * The thresholds are the same ones `world/wear.ts` reasons about — 3 m is
 * understory, 18 m is an emergent a band leaves standing.
 */
export function namePlant(kind: string, height: number): { name: string; note?: string } {
  if (kind === 'tuft') return { name: 'a tussock of grass' };
  if (kind === 'shrub') return { name: 'a thicket', note: say(height) + ' of it' };
  // the kinds that answer a place rather than fill it: reeds mean standing
  // water, a rosette means drought, a cushion means wind, a fern means shade,
  // a snag means time. Named for what they are, never for what that implies —
  // the world states, it does not explain.
  if (kind === 'reed') return { name: 'a stand of reeds', note: say(height) + ' of it' };
  if (kind === 'rosette') return { name: 'a spined rosette', note: say(height) + ' across' };
  if (kind === 'cushion') return { name: 'a cushion of alpine growth' };
  if (kind === 'fern') return { name: 'a stand of ferns' };
  if (kind === 'flower') return { name: 'a flush of wildflowers' };
  if (kind === 'snag') {
    return { name: 'a dead tree, still standing', note: say(height) + ' of bare wood' };
  }
  if (kind === 'palm') return { name: 'a palm', note: say(height) };
  if (kind === 'willow') {
    return { name: height < 8 ? 'a young willow' : 'a willow', note: say(height) };
  }
  if (kind === 'columnar') {
    return { name: height < 13 ? 'a young poplar' : 'a river poplar', note: say(height) };
  }
  // The seven v0.5 adds. Without these they all fall through to the broadleaf
  // return below, which does not fail — it just tells a god that a cactus is
  // "a broadleaf tree". A fallthrough that names the unknown after one
  // particular known thing is the worst possible break of law 12, because it
  // is confidently wrong and nothing looks broken.
  if (kind === 'succulent') {
    return { name: height < 2 ? 'a young column of cactus' : 'a column of cactus',
             note: say(height) };
  }
  if (kind === 'plume') {
    return { name: 'a tussock of bunchgrass', note: say(height) + ' to the seed heads' };
  }
  if (kind === 'broom') return { name: 'a sheaf of broom', note: say(height) + ' of it' };
  if (kind === 'creeper') return { name: 'a mat of creeping strand' };
  if (kind === 'umbel') {
    return { name: 'a flat pale umbel on a bare stalk', note: say(height) };
  }
  if (kind === 'krummholz') {
    return { name: 'a conifer laid flat by the wind', note: say(height) + ' of it' };
  }
  if (kind === 'candle') {
    return { name: 'a rosette bolted to a flowering mast', note: say(height) + ' of bloom' };
  }
  /*
   * The tree thresholds doubled with the trees (2026-08-21, `world/chunk.ts`'s
   * height law). They are the same JUDGEMENTS — a young tree, a great old one
   * — against a world whose canopy is now roughly twice as tall; left where
   * they were, every tree on the island would have been "great" and none of
   * them young.
   */
  /*
   * "…and nobody fells this one" is the naming half of a rule the world
   * already enforces: `world/wear.ts` spares plants over its `SPARED` when a
   * camp clears the ground it lives on. The two numbers must agree, and when
   * the trees roughly doubled (2026-08-21, `world/chunk.ts`'s height law) this
   * one was left behind and began calling trees emergent that the people were
   * busy felling.
   *
   * It is duplicated rather than imported ON PURPOSE: this module has no
   * imports at all — it is the one place that turns a thing into a sentence,
   * and it stays free of the world so a name can never depend on a simulation
   * state. `test/attention.ts` holds the two in step instead.
   */
  const EMERGENT = 36;                 // === world/wear.ts SPARED
  // Young is a sixth of emergent, not a third: at a third (12 m) every
  // broadleaf on the island read as "a sapling" — the grown broadleaf tops
  // out at 14.6 m drawn and the oak at 10.2 (HANDOFF 2026-08-21), so the
  // first tree a player pointed at over the camp was a sapling eleven metres
  // tall. Measured by pointing, 2026-08-22: ten of the ten nearest trees to
  // the gate camp, 7–19 m, were saplings. Six metres is a tree a person can
  // still see over.
  const old = height >= EMERGENT, young = height < EMERGENT / 6;
  if (kind === 'conifer') {
    return {
      name: young ? 'a young conifer' : old ? 'a great conifer' : 'a conifer',
      note: old ? `${say(height)} of it, and nobody fells this one` : say(height),
    };
  }
  if (kind === 'spreading') {
    return {
      name: young ? 'a young oak' : old ? 'a great spreading oak' : 'a spreading oak',
      note: old ? `${say(height)} of it, and nobody fells this one` : say(height),
    };
  }
  return {
    name: young ? 'a sapling' : old ? 'an old broadleaf' : 'a broadleaf tree',
    note: old ? `${say(height)} of it, and nobody fells this one` : say(height),
  };
}

/** Standing stone or loose scree — the island's geology, at hand. */
export function nameRock(big: boolean, height: number): { name: string; note?: string } {
  return big
    ? { name: 'an outcrop of stone', note: say(height) + ' above the ground' }
    : { name: 'loose scree' };
}

/**
 * The things that lie on the ground rather than grow out of it (Design III
 * §28: if it meaningfully exists, you should be able to point at it).
 *
 * Named for what they are and never for what they imply. A dead branch is not
 * "fuel" here even though the fire will read it that way — the world states,
 * and whoever is looking draws the conclusion.
 */
export function nameLitter(kind: string, size: number): { name: string; note?: string } {
  if (kind === 'log') {
    return { name: 'a fallen log', note: say(size) + ' of it, going back to the ground' };
  }
  if (kind === 'clump') return { name: 'a clump of sedge' };
  if (kind === 'stick') {
    return size > 1.4
      ? { name: 'a dead branch', note: say(size) }
      : { name: 'a scatter of twigs' };
  }
  // …and the seven things a patch of ground can be known for.
  //
  // This function takes a plain `string`, so the build did NOT break when the
  // litter lattice went from four kinds to eleven — which is worse than if it
  // had: every shell, bone, fern, flower, mushroom, moss and seed head fell
  // through to the stone branch below and was named "a pebble" or "a fist of
  // stone" by its size. Silent, confident and wrong. If an eighth is added,
  // `KIND_NAME` in `world/litter.ts` is the list to check against.
  if (kind === 'shell') return { name: 'a shell', note: 'something lived in this' };
  if (kind === 'bone') {
    return size > 1.4
      ? { name: 'a long bone', note: 'bleached, and nobody came back for it' }
      : { name: 'a bone' };
  }
  if (kind === 'fern') return { name: 'a fern', note: 'it likes the shade and the wet' };
  if (kind === 'flower') return { name: 'flowers' };
  if (kind === 'mushroom') return { name: 'mushrooms', note: 'up overnight' };
  if (kind === 'moss') {
    return { name: 'a cushion of moss', note: 'the ground here never dries' };
  }
  if (kind === 'seedhead') return { name: 'seed heads', note: 'the grass has gone over' };
  return size > 0.6
    ? { name: 'a fist of stone', note: say(size) + ' across' }
    : { name: 'a pebble' };
}

/**
 * A structure, named by what it is and how much of it is left. The 0.3 that
 * divides standing from fallen is `render/structures.ts`'s own RUIN.
 */
export function nameStructure(
  type: string, condition: number, lit: boolean,
  /**
   * How many functional fires stand on this ground (within hearth range of
   * this one), this one included. Two or more and the note says so — a
   * settlement of several households should read as one from any of its
   * fires (law 12; the sprawl law made the constellation, this names it).
   */
  firesOnGround = 1,
): { name: string; note?: string } {
  const what = type === 'hearth' ? 'a hearth' : type === 'shelter' ? 'a shelter' : 'a storage rack';
  if (condition < 0.3) return { name: what, note: 'fallen in, and nobody has mended it' };
  if (type === 'hearth') {
    if (!lit) return { name: what, note: 'cold' };
    if (firesOnGround >= 2) {
      const words = ['', '', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight',
                     'nine', 'ten', 'eleven', 'twelve'];
      const n = words[firesOnGround] ?? 'many';
      return { name: what, note: `somebody lives here — one of ${n} fires on this ground` };
    }
    return { name: what, note: 'somebody lives here' };
  }
  return { name: what, note: condition < 0.7 ? 'weathered' : undefined };
}

/**
 * A person. **No number about a person may appear here** — the same rule the
 * follow panel keeps, for the same reason: a life is not a readout.
 * `test/attention.ts` asserts no digit reaches this string.
 */
export function namePerson(name: string, people?: string): { name: string; note?: string } {
  return { name, note: people ? `of the ${people}` : undefined };
}

const HERD: Record<string, string> = {
  grazer: 'a herd at grass',
  browser: 'browsers in the scrub',
  small_game: 'small game',
  predator: 'something that hunts',
  scavenger: 'scavengers',
  fish: 'fish',
};

export const nameHerd = (species: string): { name: string; note?: string } =>
  ({ name: HERD[species] ?? 'animals' });

/**
 * ONE animal — the individual, not its herd.
 *
 * A person is pointable one at a time and an animal was not: the finest thing
 * a god could mean was *those* deer, never *that* deer, though the renderer
 * has drawn up to 64 separate bodies per herd all along. Law 12 says if a
 * thing meaningfully exists you should be able to point at it.
 *
 * The name says a body, not a population, and it stays in the world's voice —
 * no number about it, for the reason `test/follow.ts` guards for people: the
 * moment the chrome says `47 HP` the player is auditing livestock instead of
 * watching an animal. What it MAY carry is what the eye could actually see,
 * so `apart` supplies the same "nothing about this one is ordinary" clause a
 * promoted tree gets, and nothing here can carry a hand (law 10).
 */
const ONE: Record<string, string> = {
  grazer: 'one of the herd',
  browser: 'a browser',
  small_game: 'something small in the grass',
  predator: 'a hunter',
  scavenger: 'a scavenger',
  fish: 'a fish',
};

/**
 * What the eye can see of ONE animal, when the renderer has drawn it as an
 * individual rather than as a body in a herd.
 *
 * Every field is a pure function of `(herd seed, index)` — nothing is stored,
 * nothing is promoted into an entity, and `render/wildlife.ts` derived all of
 * it to draw the body in the first place. This is only the part that was being
 * thrown away.
 */
export interface SeenAnimal {
  /** Already player-facing: 'red deer', 'wild boar', 'great cat'. */
  kind: string;
  calf: boolean;
  horned: boolean;
  morph: 'pale' | 'dark' | null;
}

/**
 * TWO STRICT RULES, and both are the same rule wearing different clothes.
 *
 * **No number may appear.** `test/follow.ts` guards this for people and the
 * reason generalises: a number invites arithmetic, and a god who is doing
 * arithmetic about a deer has stopped looking at the deer.
 *
 * **Nothing here may claim a SIMULATED difference.** A sub-kind is drawn from
 * the herd seed and `sim/wildlife` treats every member of an archetype
 * identically — a red deer and a fallow deer eat, breed and die by the same
 * numbers. So this may say what the animal LOOKS like and must never say what
 * it does, is worth, or is good at. The day species become genuinely simulated
 * that constraint lifts, and not one day before it: law 6 says a reading must
 * agree with the thing it reads, and a card that invented a difference the
 * simulation does not have would be a lie the player could act on.
 */
export const nameAnimal = (
  species: string, alone: boolean, seen?: SeenAnimal,
): { name: string; note?: string } => {
  if (!seen) {
    return alone
      // a lone animal is a different sight from one of forty, and the count is
      // the census's, so saying "alone" costs nothing and states no number
      ? { name: ONE[species] ?? 'an animal', note: 'alone' }
      : { name: ONE[species] ?? 'an animal' };
  }
  // Only what the eye could have seen, in the order it would be noticed.
  const note = seen.calf ? 'a calf, beside its mother'
    : seen.horned ? 'antlered'
      : seen.morph === 'pale' ? 'pale, and the herd is not'
        : seen.morph === 'dark' ? 'dark, and the herd is not'
          : alone ? 'alone' : undefined;
  return { name: seen.kind, note };
};

/**
 * A trail, named by how much travel it has carried — the same strength
 * `render/paths.ts` widens it by, said in words instead of metres.
 */
export function nameTrail(strength: number): { name: string; note?: string } {
  if (strength > 0.85) return { name: 'a road', note: 'walked for generations' };
  if (strength > 0.5) return { name: 'a trail', note: 'well walked' };
  return { name: 'a footpath', note: 'faint' };
}

const ANCHOR_STAGE: Record<string, string> = {
  bare: 'nobody has found it yet',
  footpath: 'feet have worn a way to it',
  offerings: 'they leave things here',
  cairn: 'they have raised a cairn over it',
  shelter: 'they have roofed it',
  shrine: 'it is a shrine now',
  temple: 'it is a temple now',
};

export const nameAnchor = (stage: string): { name: string; note?: string } =>
  ({ name: 'your stone', note: ANCHOR_STAGE[stage] });

/** Bare ground, named by the country it belongs to. */
export const nameGround = (biome: string): { name: string; note?: string } =>
  ({ name: biome.replace(/_/g, ' ') });

/**
 * How wide a verb goes when it is channelled into a thing.
 *
 * Narrow, always: choosing a thing *is* the statement that you mean this and
 * not the hillside. The floor is the bottom of the focus ladder's `presence`
 * band, because five metres is the narrowest register the simulation has, and
 * the ceiling keeps a herd from quietly becoming a grove cast. A god who
 * wants the valley does not select anything — it points at the ground, which
 * is the whole of the interface.
 */
export const CHANNEL_MIN = 5, CHANNEL_MAX = 40;

export const channelRadius = (size: number): number =>
  Math.min(CHANNEL_MAX, Math.max(CHANNEL_MIN, size * 2));

/**
 * Is this a thing a verb can be channelled into, or is it just the world?
 *
 * A **landmark** is deliberately on the world's side of that line. It is named
 * and it can be meant, but a god who blesses a mountain means the mountain,
 * and `channelRadius` would collapse the cast onto forty metres of it — the
 * ceiling exists precisely to stop a herd quietly becoming a grove cast, and a
 * 2,500 m scarp is further the wrong way still. So a landmark keeps the
 * wheel's focus, exactly as bare ground does. What it is not is anonymous.
 */
export const isThing = (t: Target | null): t is Target =>
  t !== null && t.kind !== 'ground' && t.kind !== 'landmark';

/** One line for the chrome: what you are looking at, and what is true of it. */
export const describeTarget = (t: Target): string =>
  t.note ? `${t.name} — ${t.note}` : t.name;
